import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { useWerkveld } from "@/context/WerkveldContext";
import { useLiveStream, LiveStatus } from "@/hooks/useLiveStream";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { useAuth } from "@/context/AuthContext";
import { MANAGEMENT, heeftRol } from "@/lib/rollen";

// Zelfde kantoorrollen als backend routes/fase4.py DOSSIER_ROLLEN — andere rollen krijgen 403 op /vandaag, dus niet eens vragen
const KANTOOR_ROLLEN = new Set([...MANAGEMENT, "kcc", "salesteam"]);

const KLEUR = { groen: "bg-emerald-500", oranje: "bg-amber-500", rood: "bg-rose-600", grijs: "bg-slate-300" };
const som = (o) => Object.values(o || {}).reduce((a, b) => a + b, 0);

// Voorstel 11 — werkbord 'Vandaag': mijn zaken, wacht op ons (op SLA), SLA rood/oranje, losse mails, betalingsbeloftes vandaag. Live via SSE, polling als fallback.
export function WerkbordPaneel() {
  const { params } = useWerkveld();
  const { user } = useAuth();
  const kantoor = heeftRol(user, [...KANTOOR_ROLLEN]);
  const [d, setD] = useState(null);
  const laad = useCallback(() => (kantoor ? api.get("/vandaag", { params, bptStil: true }).then((r) => setD(r.data)).catch((e) => setD((o) => o || { fout: true, stop: e?.response?.status === 403 })) : Promise.resolve()), [params, kantoor]);
  useEffect(() => { laad(); }, [laad]);
  const { verbonden } = useLiveStream(() => laad(), { actief: !!d?.flag_aan });
  useLiveRefresh(laad, [laad], { actief: kantoor && !verbonden && !d?.stop });  // 403 (geen kantoorrol) → niet blijven pollen
  if (!d || d.fout) return null;
  const tegel = (label, waarde, tid, kleur = "") => (
    <div className={`rounded-lg border px-3 py-2 ${kleur}`} data-testid={tid}><div className="text-[11px] text-slate-500">{label}</div><div className="text-xl font-semibold">{waarde}</div></div>
  );
  return (
    <Card className="p-3 space-y-3" data-testid="werkbord-paneel">
      <div className="flex items-center gap-2"><span className="font-semibold text-sm">Vandaag — eerst oppakken</span><LiveStatus verbonden={verbonden} tid="werkbord-live" />
        <span className="ml-auto text-[11px] text-slate-400">SLA-bewaking {d.laatste_sla_run ? String(d.laatste_sla_run.op).slice(11, 16) : "nog niet gedraaid"}{!d.flag_aan ? " · werkbord_live_v1 uit" : ""}</span></div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
        {tegel("Mijn zaken", d.mijn_totaal, "werkbord-mijn")}
        {tegel("Wacht op ons", d.wacht_op_ons.length, "werkbord-wacht")}
        {tegel("Termijn verstreken", som(d.sla_rood), "werkbord-rood", som(d.sla_rood) ? "border-rose-300 bg-rose-50" : "")}
        {tegel("Bijna verstreken", som(d.sla_oranje), "werkbord-oranje", som(d.sla_oranje) ? "border-amber-300 bg-amber-50" : "")}
        {tegel("Mails zonder koppeling (48 u)", d.mails_zonder_koppeling_48u, "werkbord-mails")}
      </div>
      {d.wacht_op_ons.length > 0 && (
        <div className="text-xs space-y-1" data-testid="werkbord-wacht-lijst">
          <div className="font-medium">Klant wacht op ons — oplopend op termijn</div>
          {d.wacht_op_ons.slice(0, 8).map((w) => (
            <div key={w.debiteurnummer} className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${KLEUR[w.sla?.status] || KLEUR.grijs}`} />
              {w.klant_id ? <Link to={`/klantdossier/${w.klant_id}`} className="underline font-medium">{w.klant_naam || w.debiteurnummer}</Link> : <span className="font-medium">{w.klant_naam || w.debiteurnummer}</span>}
              <span className="text-slate-500 truncate">{w.volgende_actie || w.label}</span>
              <span className="ml-auto text-slate-400">{w.eigenaar || "geen eigenaar"} · {w.sla?.deadline ? String(w.sla.deadline).slice(0, 10) : "—"}</span>
            </div>
          ))}
        </div>
      )}
      {d.mijn_zaken.length > 0 && (
        <div className="text-xs space-y-1" data-testid="werkbord-mijn-lijst">
          <div className="font-medium">Mijn zaken</div>
          {d.mijn_zaken.slice(0, 6).map((z) => <div key={z.id} className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${KLEUR[z.sla?.status] || KLEUR.grijs}`} /><span className="font-mono text-slate-500">{z.nummer}</span><span className="truncate">{z.onderwerp}</span><Link to="/zaken" className="ml-auto underline text-slate-500">zaken</Link></div>)}
        </div>
      )}
      {d.betalingsbeloftes_vandaag.length > 0 && <div className="text-xs" data-testid="werkbord-beloftes">Betalingsbeloftes die vandaag vervallen: {d.betalingsbeloftes_vandaag.map((b) => b.klant_naam || b.debiteurnummer).join(", ")}</div>}
    </Card>
  );
}
